import type { ErrorType, ErrorDetails } from "../types/errors";
import { CategorizedError } from "../types/errors";

const ERROR_MESSAGES: Record<ErrorType, { title: string; message: string }> = {
  offline: {
    title: "No Internet Connection",
    message: "Check your network and try again",
  },
  "server-error": {
    title: "Server Unavailable",
    message: "Our servers are having trouble. Please try again in a moment",
  },
  "database-error": {
    title: "Unable to Load Data",
    message: "We couldn't connect to the database. Please refresh the page",
  },
  timeout: {
    title: "Request Timed Out",
    message: "The server took too long to respond. Try again",
  },
  "auth-required": {
    title: "Sign In Required",
    message: "Please sign in to view this content",
  },
  forbidden: {
    title: "Access Denied",
    message: "You don't have permission to view this",
  },
  "not-found": {
    title: "Not Found",
    message: "The content you're looking for doesn't exist",
  },
  "config-error": {
    title: "Configuration Error",
    message: "Something went wrong. Please contact support",
  },
  unknown: {
    title: "Something Went Wrong",
    message: "An unexpected error occurred. Please try again",
  },
};

export function mapErrorToType(error: any): ErrorType {
  // Check if already categorized
  if (error instanceof CategorizedError) {
    return error.type;
  }

  // Check if offline
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "offline";
  }

  // Check error message patterns
  const errorMessage = String(error?.message || error || "").toLowerCase();

  if (
    errorMessage.includes("err_connection_refused") ||
    errorMessage.includes("connection refused")
  ) {
    return "server-error";
  }

  if (
    errorMessage.includes("network") ||
    errorMessage.includes("failed to fetch") ||
    errorMessage.includes("networkerror")
  ) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return "offline";
    }
    return "server-error";
  }

  if (errorMessage.includes("timeout") || errorMessage.includes("timed out")) {
    return "timeout";
  }

  if (
    errorMessage.includes("firebase") ||
    errorMessage.includes("firestore") ||
    errorMessage.includes("not initialized")
  ) {
    return "database-error";
  }

  if (
    errorMessage.includes("unauthorized") ||
    errorMessage.includes("401") ||
    errorMessage.includes("sign in")
  ) {
    return "auth-required";
  }

  if (
    errorMessage.includes("forbidden") ||
    errorMessage.includes("403") ||
    errorMessage.includes("access denied")
  ) {
    return "forbidden";
  }

  if (errorMessage.includes("not found") || errorMessage.includes("404")) {
    return "not-found";
  }

  // Check status codes
  const statusCode = error?.statusCode || error?.status;
  if (statusCode) {
    if (statusCode === 401) return "auth-required";
    if (statusCode === 403) return "forbidden";
    if (statusCode === 404) return "not-found";
    if (statusCode >= 500) return "server-error";
  }

  // Check for 500-level errors in message
  if (
    errorMessage.includes("500") ||
    errorMessage.includes("502") ||
    errorMessage.includes("503") ||
    errorMessage.includes("504") ||
    errorMessage.includes("internal server")
  ) {
    return "server-error";
  }

  return "unknown";
}

export function getErrorDetails(error: any): ErrorDetails {
  const type = mapErrorToType(error);
  const defaults = ERROR_MESSAGES[type];

  return {
    type,
    title: defaults.title,
    message: defaults.message,
  };
}

export function categorizeError(
  error: any,
  fallbackType: ErrorType = "unknown",
): CategorizedError {
  if (error instanceof CategorizedError) {
    return error;
  }

  const type = mapErrorToType(error);
  const message = error?.message || String(error) || "An error occurred";
  const statusCode = error?.statusCode || error?.status;

  return new CategorizedError(
    message,
    type === "unknown" ? fallbackType : type,
    statusCode,
  );
}
